import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Tooltip,
  Legend,
} from 'chart.js';
import { Bar } from 'react-chartjs-2';
import { nodesToPdf } from '../utils/pdf';
import '../styles/Reports.scss';
import SubMinWarning from '../components/SubMinWarning';
import api from '../services/api';
import { chartColors, themeVars } from '../config/theme';

const CHART = chartColors('skills_development');

ChartJS.register(CategoryScale, LinearScale, BarElement, Tooltip, Legend);

const fmt = (n) => (Math.round(n * 100) / 100).toFixed(2);

const SDReportsChart = () => {
  const navigate = useNavigate();
  const reportRef = useRef(null);

  const downloadPDF = async () => {
    const hide = ['.reports-download-btn', '.reports-back'].map((sel) => document.querySelector(sel)).filter(Boolean);
    await nodesToPdf(reportRef.current, 'skills-development-report.pdf', {
      beforeCapture: () => hide.forEach((n) => { n.style.visibility = 'hidden'; }),
      afterCapture: () => hide.forEach((n) => { n.style.visibility = 'visible'; }),
    });
  };
  const [results, setResults] = useState(null);
  const [totalPoints, setTotalPoints] = useState(0);
  const [spendSummary, setSpendSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [indicators, setIndicators] = useState([]);

  useEffect(() => {
    api.get('/core/scorecard/get_scorecard/?element=skills_development')
      .then((res) => {
        setResults(res.data.results);
        setTotalPoints(res.data.total_points);
        setSpendSummary(res.data.spend_summary || null);
        setLoading(false);
        setIndicators(res.data.indicators || []);  //To pull indicators from backend instead of it being hardcoded in the frontend    
      })
      .catch(() => setLoading(false));
  }, []);

  const resultMap = results
    ? Object.fromEntries(results.map((r) => [r.id, r]))
    : {};


  const chartData = {
    labels: indicators.map((ind) =>
      ind.label.length > 35 ? ind.label.slice(0, 35) + '…' : ind.label
    ),
    datasets: [
      {
        label: 'Points Achieved',
        data: indicators.map((ind) => resultMap[ind.id]?.points ?? 0),
        backgroundColor: CHART.fill,
        borderColor: CHART.border,
        borderWidth: 1,
        borderRadius: 4,
      },
      {
        label: 'Max Points',
        data: indicators.map((ind) => ind.points),
        backgroundColor: CHART.track,
        borderColor: CHART.trackBorder,
        borderWidth: 1,
        borderRadius: 4,
      },
    ],
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    animation: false,
    plugins: {
      legend: { position: 'top' },
      tooltip: {
        callbacks: {
          title: (items) => indicators[items[0].dataIndex].label,
        },
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        ticks: { stepSize: 1 },
        title: { display: true, text: 'Points' },
      },
      x: {
        ticks: { font: { size: 11 } },
      },
    },
  };

  const maxPoints = indicators.reduce((s, ind) => s + (ind.points || 0), 0);

  return (
    <div className="reports-container themed" style={themeVars('skills_development')} ref={reportRef}>
      <div className="reports-header">
        <button className="reports-back" onClick={() => navigate('/')}>← Back</button>
        <div>
          <h1>Reports</h1>
          <p>B-BBEE — Skills Development Performance Overview</p>
        </div>
        <button className="reports-back reports-download-btn" style={{ marginLeft: 'auto' }} onClick={downloadPDF}>
          Download PDF
        </button>
      </div>

      <div className="reports-body">
      {loading && <p className="reports-loading">Loading scorecard data…</p>}

      {!loading && !results && (
        <div className="reports-empty">
          No scorecard data found. Go to the{' '}
          <button
            className="reports-inline-link"
            onClick={() => navigate('/skills-development/scorecard')}
          >
            Skills Development Scorecard
          </button>{' '}
          to calculate your results first.
        </div>
      )}

      {!loading && results && (
        <>
          <div className="reports-summary">
            <div className="reports-summary-card reports-summary-card--primary">
              <div className="reports-summary-head">
                <span className="reports-summary-label">Total Skills Development Points</span>
              </div>
              <div className="reports-summary-score">{fmt(totalPoints)} <span>/ {maxPoints || '—'}</span></div>
              <div className="reports-summary-ctx">
                <span>Full score <b>{maxPoints || '—'}</b></span>
                <span>Gap <b>{(maxPoints - totalPoints).toFixed(2)}</b></span>
              </div>
            </div>

            {(() => {
              const pct = Math.round((totalPoints / maxPoints || '—') * 100);
              const offset = 264 - (264 * Math.min(pct, 100) / 100);
              return (
                <div className="reports-summary-card">
                  <div className="reports-summary-head">
                    <span className="reports-summary-label">Achievement</span>
                    <span className={`reports-summary-risk${pct >= 60 ? ' reports-summary-risk--ok' : ''}`}>
                      <i></i>{pct >= 60 ? 'On Track' : 'At Risk'}
                    </span>
                  </div>
                  <div className="reports-summary-gauge">
                    <svg viewBox="0 0 200 110" preserveAspectRatio="xMidYMid meet">
                      <defs>
                        <linearGradient id="gaugeGradSD" x1="0" y1="0" x2="1" y2="0">
                          <stop offset="0%" stopColor={CHART.fill} />
                          <stop offset="100%" stopColor={CHART.fill} />
                        </linearGradient>
                      </defs>
                      <path className="gauge-track" d="M 16 100 A 84 84 0 0 1 184 100" />
                      <path className="gauge-fill" d="M 16 100 A 84 84 0 0 1 184 100"
                        stroke="url(#gaugeGradSD)"
                        strokeDasharray="264"
                        strokeDashoffset={offset} />
                    </svg>
                    <div className="reports-summary-gauge-label">
                      <span className="v">{pct}<span className="pct">%</span></span>
                      <small>vs 100% full score</small>
                    </div>
                  </div>
                </div>
              );
            })()}

            <div className="reports-summary-card">
              <div className="reports-summary-head">
                <span className="reports-summary-label">Indicators Fully Achieved</span>
              </div>
              <div className="reports-summary-score">
                {results.filter((r) => {
                  const ind = indicators.find((i) => i.id === r.id);
                  return ind && r.points >= ind.points;
                }).length}
                <span> / {indicators.length}</span>
              </div>
            </div>
          </div>

          {spendSummary && (
            <div className="sc-table-outer">
              <h2 className="sc-spend-title" style={{ fontSize: '13px', marginBottom: '8px' }}>Spend Breakdown by Programme</h2>
              <div className="sc-table-wrapper">
                <table className="sc-table sc-spend-table" style={{ fontSize: '11px' }}>
                  <thead>
                    <tr>
                      <th style={{ padding: '8px 10px' }}>Programme</th>
                      <th style={{ padding: '8px 10px' }}>African Male</th>
                      <th style={{ padding: '8px 10px' }}>African Female</th>
                      <th style={{ padding: '8px 10px' }}>Coloured Male</th>
                      <th style={{ padding: '8px 10px' }}>Coloured Female</th>
                      <th style={{ padding: '8px 10px' }}>Indian Male</th>
                      <th style={{ padding: '8px 10px' }}>Indian Female</th>
                      <th style={{ padding: '8px 10px' }}>Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {spendSummary.map((row) => {
                      const demos = ['african_male', 'african_female', 'coloured_male', 'coloured_female', 'indian_male', 'indian_female'];
                      const rowTotal = row.total != null
                        ? row.total
                        : demos.reduce((s, d) => s + (row[d] || 0), 0);
                      const targetTotal = demos.reduce((s, d) => s + (row.targets?.[d] || 0), 0);
                      return (
                        <React.Fragment key={row.program}>
                          <tr>
                            <td style={{ padding: '6px 10px' }}>{row.program}</td>
                            {demos.map((d) => (
                              <td key={d} style={{ padding: '6px 10px' }}>{row[d] != null ? row[d].toLocaleString() : '—'}</td>
                            ))}
                            <td style={{ padding: '6px 10px' }}><strong>{rowTotal.toLocaleString()}</strong></td>
                          </tr>
                          {row.program !== 'Absorption' && (
                            <tr className="sc-row--target">
                              <td style={{ padding: '6px 10px' }}>Target</td>
                              {demos.map((d) => (
                                <td key={d} style={{ padding: '6px 10px' }}>
                                  {row.targets?.[d] != null ? Math.round(row.targets[d]).toLocaleString() : '—'}
                                </td>
                              ))}
                              <td style={{ padding: '6px 10px' }}><strong>{row.targets?.total != null ? Math.round(row.targets.total).toLocaleString() : Math.round(targetTotal).toLocaleString()}</strong></td>
                            </tr>
                          )}
                        </React.Fragment>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          <SubMinWarning results={results} />

          <div className="reports-chart-card">
            <h2 className="reports-chart-heading">Skills Development — Points Achieved vs Maximum</h2>
            <p className="reports-chart-sub">Per indicator breakdown across all Skills Development initiatives</p>
            <div style={{ height: '260px', position: 'relative' }}>
              <Bar data={chartData} options={chartOptions} />
            </div>
          </div>
        </>
      )}
      </div>
    </div>
  );
};

export default SDReportsChart;
